import React, { useState } from "react";
import ShareDialog from "./ShareDialog";
import RadarChart from "./RadarChart";
import PlayerRow from "../sport/components/PlayerRow";
import {
  teamStats,
  avgOverall,
  teamStrength,
  ratingLetter,
  projectSeason,
  chemistryBonus,
} from "../sport/projection";
import { getBenchmarkProjections } from "../sport/benchmarks";
import TeamLogo from "../sport/components/TeamLogo";
import CloseIcon from "./icons";
import { useAuth } from "../contexts/AuthContext";
import SPORT from "../sport/config";

export default function ResultsModal({
  open,
  onClose,
  roster,
  slots,
  onPlayAgain,
  isPersonalBest,
}) {
  const { user } = useAuth();
  const [showShare, setShowShare] = useState(false);

  if (!open) return null;

  const stats = teamStats(roster, slots);
  const avg = avgOverall(roster);
  const strength = teamStrength(roster, slots);
  const season = projectSeason(roster, slots);
  const rating = ratingLetter(season);
  const chemistry = chemistryBonus(roster);
  const TOTAL_LEAGUE_SIZE = SPORT.leagueSize;
  const benchmarks = getBenchmarkProjections(slots);

  const sortedAll = [
    ...benchmarks.map((b) => ({ ...b, isUser: false })),
    {
      isUser: true,
      wins: season.wins,
      draws: season.draws,
      losses: season.losses,
      rating,
      strength,
    },
  ].sort((a, b) => {
    const pa = a.wins * 3 + a.draws;
    const pb = b.wins * 3 + b.draws;
    if (pa !== pb) return pb - pa; // 1st: points
    if (b.wins !== a.wins) return b.wins - a.wins; // 2nd: wins
    if (a.losses !== b.losses) return a.losses - b.losses; // 3rd: fewer losses
    return b.strength - a.strength; // 4th: strength as last tiebreak
  });

  const userPos = sortedAll.findIndex((e) => e.isUser);
  const minBmStrength =
    benchmarks.length > 0 ? Math.min(...benchmarks.map((b) => b.strength)) : 0;

  const standings = sortedAll.map((entry, idx) => {
    let displayRank = idx + 1;
    // If user is below every benchmark, give them a "real league" position
    if (
      entry.isUser &&
      userPos === sortedAll.length - 1 &&
      benchmarks.length > 0
    ) {
      const gap = Math.max(0, minBmStrength - entry.strength);
      displayRank = Math.min(
        TOTAL_LEAGUE_SIZE,
        Math.max(idx + 2, Math.round(idx + 1 + gap)),
      );
    }
    return { ...entry, displayRank };
  });

  // Insert an ellipsis row when there's a numerical gap between adjacent entries
  let ellipsisBefore = -1;
  for (let i = 1; i < standings.length; i++) {
    if (standings[i].displayRank > standings[i - 1].displayRank + 1) {
      ellipsisBefore = i;
      break;
    }
  }

  const orderedPlayers = slots
    .map((slot) => ({
      slotId: slot.id,
      player: roster[slot.id],
    }))
    .filter((x) => x.player);

  return (
    <div
      className="fixed inset-0 z-50 bg-black/70 backdrop-blur-md
                 grid place-items-center p-6"
      onClick={onClose}
    >
      <div
        className="bg-zinc-950 border border-zinc-800 rounded-2xl
                   w-full max-w-5xl max-h-[92vh] overflow-y-auto scrollbar-fancy
                   relative animate-scale-in"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute top-4 right-4 w-9 h-9 grid place-items-center rounded-full
                     text-zinc-500 hover:text-zinc-100 hover:bg-zinc-900 transition z-10"
          aria-label="Close"
        >
          <CloseIcon />
        </button>

        <div className="p-8 md:p-10">
          {/* W/D/L header */}
          <div className="text-center mb-10">
            {isPersonalBest && (
              <div className="flex justify-center mb-4">
                <span
                  className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider
                             text-emerald-300 bg-emerald-500/10 border border-emerald-500/40 rounded-full px-3 py-1"
                >
                  <StarIcon className="w-3.5 h-3.5" />
                  New personal best
                </span>
              </div>
            )}
            <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-3">
              {SPORT.copy.resultsHeading}
            </div>
            <div className="flex items-baseline justify-center gap-8">
              <Stat label="Wins" value={season.wins} color="text-emerald-400" />
              {SPORT.hasDraws && (
                <>
                  <div className="text-zinc-700 text-2xl">·</div>
                  <Stat
                    label="Draws"
                    value={season.draws}
                    color="text-zinc-300"
                  />
                </>
              )}
              <div className="text-zinc-700 text-2xl">·</div>
              <Stat label="Losses" value={season.losses} color="text-red-400" />
            </div>
          </div>

          {!user && (
            <div
              className="max-w-md mx-auto mb-6 text-center text-sm text-amber-200
                         bg-amber-500/10 border border-amber-500/30 rounded-xl px-4 py-3"
            >
              Sign in to save your score to the leaderboard.
            </div>
          )}

          {/* Share and Play Again */}
          <div className="flex items-center justify-center gap-3 mb-10">
            <button
              type="button"
              onClick={() => setShowShare(true)}
              className="px-6 py-3 rounded-full bg-zinc-900 border border-zinc-800 text-zinc-200 font-semibold text-sm hover:border-zinc-700 transition"
            >
              Share results
            </button>
            <button
              type="button"
              onClick={onPlayAgain}
              className="px-6 py-3 rounded-full bg-emerald-500 text-zinc-950 font-semibold text-sm hover:bg-emerald-400 transition"
            >
              Play again
            </button>
          </div>

          {/* Body */}
          <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] gap-8 mb-8">
            {/* Left: rating + radar */}
            <div className="bg-zinc-900/40 border border-zinc-800 rounded-2xl p-6">
              <div className="text-center mb-6">
                <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-2">
                  Team rating
                </div>
                <div className="text-8xl font-black text-emerald-400 leading-none tracking-tight">
                  {rating}
                </div>
                {chemistry.breakdown.length > 0 && (
                  <div className="mt-3 flex flex-wrap justify-center gap-1.5">
                    {chemistry.breakdown.map((b, i) => (
                      <span
                        key={i}
                        className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-500/10 border border-emerald-500/30 text-emerald-300"
                      >
                        {b.label}{" "}
                        <span className="text-emerald-400 font-bold ml-0.5">
                          +{b.value}
                        </span>
                      </span>
                    ))}
                  </div>
                )}
              </div>
              <div className="max-w-[340px] mx-auto">
                <RadarChart stats={stats} />
              </div>
            </div>

            {/* Right */}
            {/* League standings */}
            <div className="mt-10">
              <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-3 text-center">
                League standings
              </div>
              <div className="space-y-2 max-w-2xl mx-auto">
                {standings.map((entry, i) => (
                  <React.Fragment key={i}>
                    {i === ellipsisBefore && (
                      <div className="text-center text-zinc-600 text-sm py-1 tracking-[0.5em] select-none">
                        · · ·
                      </div>
                    )}
                    <div
                      className={`flex items-center gap-3 rounded-xl px-4 py-3 border transition
        ${
          entry.isUser
            ? "bg-emerald-500/10 border-emerald-500/50 shadow-lg shadow-emerald-500/10"
            : "bg-zinc-900/40 border-zinc-800"
        }`}
                    >
                      <span
                        className={`text-xs font-bold tabular-nums w-6
          ${entry.isUser ? "text-emerald-400" : "text-zinc-500"}`}
                      >
                        {entry.displayRank}.
                      </span>

                      {entry.isUser ? (
                        <div className="w-9 h-9 rounded-md bg-emerald-500/20 border border-emerald-500/40 grid place-items-center shrink-0">
                          <span className="text-[10px] font-bold tracking-wider text-emerald-400">
                            YOU
                          </span>
                        </div>
                      ) : (
                        <TeamLogo club={entry.club} size={36} />
                      )}

                      <div className="flex-1 min-w-0">
                        <div
                          className={`text-sm font-semibold truncate
            ${entry.isUser ? "text-emerald-200" : "text-zinc-100"}`}
                        >
                          {entry.isUser ? SPORT.copy.yourSquad : entry.club}
                        </div>
                        {!entry.isUser && (
                          <div className="text-xs text-zinc-500 truncate">
                            {entry.season}
                            {/* {entry.label ? (
                              <>
                                <span className="text-zinc-700 mx-1.5">·</span>
                                {entry.label}
                              </>
                            ) : null} */}
                          </div>
                        )}
                      </div>

                      <div className="text-xs tabular-nums shrink-0 flex items-center gap-1.5">
                        <span className="text-emerald-400 font-semibold">
                          {entry.wins}
                        </span>
                        {SPORT.hasDraws && (
                          <>
                            <span className="text-zinc-700">·</span>
                            <span className="text-zinc-400">{entry.draws}</span>
                          </>
                        )}
                        <span className="text-zinc-700">·</span>
                        <span className="text-red-400">{entry.losses}</span>
                      </div>

                      <span
                        className={`text-sm font-bold w-10 text-right tabular-nums shrink-0
          ${entry.isUser ? "text-emerald-300" : "text-emerald-400"}`}
                      >
                        {entry.rating}
                      </span>
                    </div>
                  </React.Fragment>
                ))}
              </div>
            </div>

            {/* Squad */}
            <div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-3">
                Squad
              </div>
              <div className="space-y-2">
                {orderedPlayers.map(({ slotId, player }) => (
                  <PlayerRow
                    key={slotId}
                    player={player}
                    selected={false}
                    disabled={false}
                    onClick={() => {}}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
      <ShareDialog
        open={showShare}
        onClose={() => setShowShare(false)}
        roster={roster}
        slots={slots}
        season={season}
        rating={rating}
        avg={avg}
      />
    </div>
  );
}

function Stat({ label, value, color }) {
  return (
    <div>
      <div className={`text-6xl font-black tabular-nums leading-none ${color}`}>
        {value}
      </div>
      <div className="text-xs uppercase tracking-wider text-zinc-500 mt-2">
        {label}
      </div>
    </div>
  );
}

function StarIcon(props) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" {...props}>
      <path d="M12 2l2.9 6.26L21.6 9l-5 4.87L17.8 21 12 17.27 6.2 21l1.2-7.13-5-4.87 6.7-.74L12 2z" />
    </svg>
  );
}
